import { useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import useListingOrgActivity from "hooks/useListingOrgActivity";
import { ListingOrgActivityTimeline } from "./ListingOrgActivityTimeline";

type Props = {
  listingId?: string;
};

const MAX_AVATARS = 3;
const POPUP_MAX_ITEMS = 8;

/**
 * "Someone else in your agency touched this listing" indicator: up to 3
 * stacked avatars of the org-mates with a shared note / meeting / task on the
 * listing (+N overflow bubble), opening a short activity timeline on click.
 *
 * Renders nothing for users without an organization and for listings with no
 * org activity, so it is safe to mount on any listing card/row. Data is
 * batched per screen by useListingOrgActivity — mounting this on 50 rows
 * costs one request.
 */
export function ListingOrgActivityIndicator({ listingId }: Props) {
  const { t } = useTranslation();
  const { activity } = useListingOrgActivity(listingId);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (!activity || activity.realtors.length === 0) return null;

  const visibleRealtors = activity.realtors.slice(0, MAX_AVATARS);
  const overflowCount = activity.realtors.length - visibleRealtors.length;
  const tooltipTitle = t("listingsBoard.orgActivity.tooltip", {
    names: visibleRealtors.map((r) => r.userName).join(", "),
    count: activity.totalCount
  });

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <Box
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(anchor ? null : e.currentTarget);
          }}
          sx={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
        >
          <AvatarGroup
            sx={{
              "& .MuiAvatar-root": {
                width: 22,
                height: 22,
                fontSize: 11,
                border: (theme) => `2px solid ${theme.palette.background.paper}`
              }
            }}
          >
            {visibleRealtors.map((realtor) => (
              <Avatar key={realtor.userId} src={realtor.userAvatar || undefined} alt={realtor.userName}>
                {realtor.userName?.charAt(0)}
              </Avatar>
            ))}
            {overflowCount > 0 && <Avatar>+{overflowCount}</Avatar>}
          </AvatarGroup>
        </Box>
      </Tooltip>

      <Popper open={Boolean(anchor)} anchorEl={anchor} placement="bottom-start" style={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
          <Paper
            elevation={8}
            sx={{ minWidth: 280, maxWidth: 360, maxHeight: 340, overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack sx={{ p: 1.25 }} spacing={0.5}>
              <Typography variant="subtitle2">{t("listingsBoard.orgActivity.popoverTitle")}</Typography>
              <ListingOrgActivityTimeline activity={activity} maxItems={POPUP_MAX_ITEMS} />
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
